/**
 * The operator's choice on a dirty vocabulary save.
 *
 * "keep" is "Não enviar": the leaves are written and `publish` is false, so
 * the first refuse can freeze the merge from before this edit. Omitting the
 * flag is reserved for callers that are not this choice (welcome, staff).
 * "send" publishes. "dismiss" writes nothing.
 */

export type VocabularyChoice = "dismiss" | "keep" | "send";

export interface VocabularyWriteCall {
  values: Record<string, unknown>;
  publish: boolean;
}

export function writeForChoice(
  choice: VocabularyChoice,
  dirty: Record<string, unknown>,
): VocabularyWriteCall | null {
  if (choice === "dismiss") return null;
  if (choice === "keep") return { values: dirty, publish: false };
  return { values: dirty, publish: true };
}

/** A failed send is retried with the flag and no leaves. */
export function retryFailedSend(): VocabularyWriteCall {
  return { values: {}, publish: true };
}

/** The publication row is read only for this boolean. The blob is not a form. */
export function readSendFailed(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return (value as { send_failed?: unknown }).send_failed === true;
}

/**
 * A publication error must not copy the reloaded leaves over the dirty form.
 * Any other reload may replace it.
 */
export function shouldReplaceForm(publicationFailed: boolean): boolean {
  return !publicationFailed;
}

export type SaveAffordance = "choice" | "retry" | "disabled";

export function saveAffordance(isDirty: boolean, sendFailed: boolean): SaveAffordance {
  if (isDirty) return "choice";
  if (sendFailed) return "retry";
  return "disabled";
}

/** One changed word is quoted. Several edits stay a single sentence. */
export function choiceMessage(dirty: Record<string, unknown>): string {
  const values = Object.values(dirty);
  const only = values.length === 1 ? values[0] : null;
  if (typeof only === "string" && only.length > 0) {
    return `O clube fica com “${only}” de qualquer forma. O app dos membros só muda se você enviar.`;
  }
  return "O clube fica com esta edição de qualquer forma. O app dos membros só muda se você enviar.";
}
